import type {GuildCompetitionGiftPanelProps} from './components/GuildCompetitionGiftPanel/types'
import {
  ActivityMemberV2,
  MiddleContestAuditionToRankRequestParams,
  MiddleContestAuditionToRankResponse,
  MiddleContestAuditionToRankState,
  MiddleContestGlobalCountryItem,
  MiddleContestGlobalCountryList,
  MiddleContestRankListItem,
  MiddleContestRankOwn,
  MiddleContestRankRequestParams,
  MiddleContestRankResponse,
  MiddleContestRankReward,
  MiddleContestRankState,
  MiddleContestRankUnion,
  MiddleContestRewardListResponse,
  MiddleContestRewardListState,
  MiddleContestCardItem,
  MiddleContestCardInfoResponse,
  MiddleContestCardStatus,
  MiddleContestHomepageResponse,
  MiddleContestHomepageState,
  MiddleContestHomepageTabItem,
  MiddleContestHomeStatus,
  MiddleContestEventGifts,
  MiddleContestHomeMatchStage,
  MiddleContestUserRole,
  MiddleContestMatchInfo,
  MiddleContestPreheatData,
  MiddleContestRewardData,
  MiddleContestRewardItem,
  MiddleContestPersonItem,
  MiddleContestPersonListResponse,
  MiddleContestPersonListState,
  MiddleContestSupportAnchor,
  MiddleContestRepechageResponse,
  MiddleContestRepechageRewardItem,
  MiddleContestRepechageState,
  MiddleContestRepechageSuccessReward,
  MiddleContestRepechageUnionInfo,
  MiddleContestTaskRecordResponse,
  MiddleContestTaskRecordRewardItem,
  MiddleContestTaskRecordState,
  MiddleContestTaskRecordTaskInfo,
  MiddleContestTaskConfigItem,
  MiddleContestTaskListResponse,
  MiddleContestTaskState,
  MiddleContestLotteryInfo,
} from './types'

export const DEFAULT_CARD_PAGE_LIMIT = 20

/** rank 接口默认分页条数 */
export const RANK_PAGE_LIMIT = 20

/** 接口 countdown 剩余秒数 -> 倒计时结束时间戳 */
export const getCountdownEndTime = (countdownSeconds: number): number => {
  if (!countdownSeconds || countdownSeconds <= 0) return 0
  return Date.now() + countdownSeconds * 1000
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** 加成卡 expire_ts（秒）→「HH:MM:SS」（天数折算进小时） */
export const formatCardExpireCountdownFromParts = (
  days: number,
  hours: number,
  minutes: number,
  seconds: number
): string => {
  const totalHours = days * 24 + hours
  return `${pad2(totalHours)}:${pad2(minutes)}:${pad2(seconds)}`
}

export const formatCardExpireCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const days = Math.floor(safe / 86400)
  const hours = Math.floor((safe % 86400) / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  return formatCardExpireCountdownFromParts(days, hours, minutes, seconds)
}

export const normalizeMatchInfo = (
  item: MiddleContestMatchInfo
): MiddleContestMatchInfo => ({
  time_text: item.time_text ?? '',
  match_name: item.match_name ?? '',
  is_progress: !!item.is_progress,
})

/** 分组晋级赛（match_info 第二段）是否进行中 */
export const isGroupAdvancementMatchInProgress = (
  matchInfo: MiddleContestMatchInfo[] = []
): boolean => !!matchInfo[1]?.is_progress

/** 海选赛或分组晋级赛（match_info 前两段）是否进行中 */
export const isAuditionOrGroupAdvancementMatchInProgress = (
  matchInfo: MiddleContestMatchInfo[] = []
): boolean =>
  !!matchInfo[MiddleContestHomeMatchStage.audition]?.is_progress ||
  !!matchInfo[MiddleContestHomeMatchStage.groupAdvancement]?.is_progress


/** 仅 not_global === true 时不展示全球赛；false / 未下发保持原样 */
export const isNotGlobal = (value?: boolean): boolean => value === true

/** 不展示全球赛时，活动礼物标题/规则改用幸运礼物文案 */
export const shouldUseIndiaLuckyGiftCopy = (notGlobal?: boolean): boolean =>
  isNotGlobal(notGlobal)

const resolveMaxMatchStage = (
  enableGlobal: boolean
): MiddleContestHomeMatchStage =>
  enableGlobal
    ? MiddleContestHomeMatchStage.nationalAssist
    : MiddleContestHomeMatchStage.groupAdvancement

/**
 * 解析当前赛程下标：
 * - home_status=2：match_info 中 is_progress 项（默认 0）
 * - home_status=3：固定为最后一程全民助力（2）；not_global === true 时上限为地区决赛
 */
export const resolveCurrentMatchStage = (
  homeStatus: MiddleContestHomeStatus,
  matchInfo: MiddleContestMatchInfo[],
  enableGlobal = true
): MiddleContestHomeMatchStage | null => {
  const maxStage = resolveMaxMatchStage(enableGlobal)
  if (homeStatus === MiddleContestHomeStatus.review) {
    return maxStage
  }
  if (homeStatus !== MiddleContestHomeStatus.inProgress) {
    return null
  }
  const progressIdx = matchInfo.findIndex((item) => item.is_progress)
  const index = progressIdx >= 0 ? progressIdx : 0
  return Math.min(index, maxStage) as MiddleContestHomeMatchStage
}

/** toast_name 中 * 后的角标文案，如「神秘人*7天」→「7天」 */
export const extractToastNameSuffix = (
  toastName: string
): string | undefined => {
  const index = toastName.lastIndexOf('*')
  if (index < 0 || index >= toastName.length - 1) {
    return undefined
  }
  const suffix = toastName.slice(index + 1).trim()
  return suffix || undefined
}

type GiftBadgeTranslate = (key: string) => string

/** 礼物卡角标：展示 toast_name * 后内容，优先走 toast_name 对应 i18n */
export const formatGiftCardBadgeLabel = (
  toastName: string,
  t: GiftBadgeTranslate
): string | undefined => {
  const suffix = extractToastNameSuffix(toastName)
  if (!suffix) {
    return undefined
  }
  const translatedName = t(toastName)
  if (translatedName !== toastName) {
    const translatedSuffix = extractToastNameSuffix(translatedName)
    if (translatedSuffix) {
      return translatedSuffix
    }
  }
  return suffix
}

export const normalizeRewardItem = (
  item: MiddleContestRewardItem
): MiddleContestRewardItem => ({
  id: item.id ?? 0,
  view_icon: item.view_icon ?? '',
  toast_name: item.toast_name ?? '',
  reward_type: item.reward_type ?? 0,
  tag: item.tag ?? '',
  privilege_id: item.privilege_id ?? null,
  quantity: item.quantity ?? 0,
  reward_value: item.reward_value ?? 0,
  has_show_effect: !!item.has_show_effect,
})

export const normalizeEventGifts = (
  payload: Pick<MiddleContestHomepageResponse, 'event_gifts'> = {}
): MiddleContestEventGifts => ({
  gift_id: payload.event_gifts?.gift_id ?? 0,
  gift_items: (payload.event_gifts?.gift_items ?? []).map(normalizeRewardItem),
})

export const normalizePreheatData = (
  data?: MiddleContestPreheatData | null
): MiddleContestPreheatData => ({
  finalist_ranking: data?.finalist_ranking ?? 0,
  finalist_reward_value: data?.finalist_reward_value ?? 0,
  group_rank_max_coin: data?.group_rank_max_coin ?? 0,
  group_rank_max_card_rate: data?.group_rank_max_card_rate ?? 0,
  like_to_score: data?.like_to_score ?? 0,
})

export const normalizeRewardData = (
  data?: MiddleContestRewardData | null
): MiddleContestRewardData => ({
  preliminary_total_coin: data?.preliminary_total_coin ?? 0,
  preliminary_winning_union_count: data?.preliminary_winning_union_count ?? 0,
  regional_final_total_coin: data?.regional_final_total_coin ?? 0,
  regional_final_winning_union_count:
    data?.regional_final_winning_union_count ?? 0,
  global_total_coin: data?.global_total_coin ?? 0,
})

export const normalizeHomepageTabList = (
  tabList?: MiddleContestHomepageTabItem[] | null
): MiddleContestHomepageTabItem[] =>
  (Array.isArray(tabList) ? tabList : []).map((item) => {
    const tabId = Number(item?.tab_id ?? 0)
    const currentTab = Number(item?.is_current_tab ?? 0)
    return {
      ...item,
      tab_id: Number.isFinite(tabId) ? tabId : 0,
      tab_name: item?.tab_name ?? '',
      is_new: !!item?.is_new,
      is_current_tab: Number.isFinite(currentTab) ? currentTab : 0,
    }
  })

/** 预热文案：金币展示（千分位） */
export const formatPreheatCoin = (value: number, fallback = '10,000'): string =>
  value > 0 ? value.toLocaleString('en-US') : fallback

/** 预热文案：加成卡倍率 */
export const formatPreheatCardRate = (
  value: number,
  fallback = '1.2'
): string => (value > 0 ? String(value) : fallback)

/** event_gifts.gift_items → 预热活动礼物卡片 */
export const mapEventGiftsToPreheatCards = (
  eventGifts: MiddleContestEventGifts
) =>
  eventGifts.gift_items.map((item, index) => ({
    id: String(item.id ?? index),
    iconUrl: item.view_icon,
    name: item.toast_name,
    price: String(item.reward_value ?? 0),
  }))

/** event_gifts → 赛程 Tab 活动礼物面板 */
export const mapEventGiftsToGiftPanelProps = (
  eventGifts: MiddleContestEventGifts
): Pick<GuildCompetitionGiftPanelProps, 'items'> => ({
  items: eventGifts.gift_items.map((item) => ({
    name: item.toast_name,
    iconUrl: item.view_icon,
    priceLabel: String(item.reward_value ?? 0),
  })),
})

export const normalizeHomepageResponse = (
  payload: MiddleContestHomepageResponse = {}
): MiddleContestHomepageState => {
  const homeStatus = Number(payload.home_status)
  let normalizedHomeStatus = Number.isNaN(homeStatus)
    ? MiddleContestHomeStatus.preheat
    : (homeStatus as MiddleContestHomeStatus)
  const notGlobal = isNotGlobal(payload.not_global)
  const enableGlobal = !notGlobal
  if (notGlobal && normalizedHomeStatus === MiddleContestHomeStatus.review) {
    normalizedHomeStatus = MiddleContestHomeStatus.inProgress
  }
  const matchInfo = (payload.match_info ?? []).map(normalizeMatchInfo)
  return {
    err_msg: payload.err_msg ?? '',
    home_status: normalizedHomeStatus,
    match_info: matchInfo,
    current_match: resolveCurrentMatchStage(
      normalizedHomeStatus,
      matchInfo,
      enableGlobal
    ),
    countdown: payload.countdown ?? 0,
    recharge_countdown: payload.recharge_countdown ?? 0,
    recharge_url: payload.recharge_url ?? '',
    year_middle_home_url: payload.year_middle_home_url ?? '',
    event_gifts: normalizeEventGifts(payload),
    lottery_id: payload.lottery_id ?? 0,
    audtion_to_next_cur_date: normalizeAuditionToNextDateString(
      payload.audtion_to_next_cur_date ?? ''
    ),
    audtion_to_next_date_list: (payload.audtion_to_next_date_list ?? [])
      .map((item) => normalizeAuditionToNextDateString(item))
      .filter(Boolean),
    preheat_data: normalizePreheatData(payload.preheat_data),
    reward_data: normalizeRewardData(payload.reward_data),
    role: Number(
      payload.role ?? MiddleContestUserRole.normal
    ) as MiddleContestUserRole,
    room_id: payload.room_id ?? 0,
    tab_list: normalizeHomepageTabList(payload.tab_list),
    not_global: notGlobal,
  }
}

/** match_info -> 赛程条组件 props */
export const mapMatchInfoToScheduleStrip = (
  matchInfo: MiddleContestMatchInfo[],
  homeStatus?: MiddleContestHomeStatus,
  enableGlobal = true
) => {
  const stages = matchInfo.slice(0, 3).map((item) => ({
    title: item.match_name,
    dateRange: item.time_text,
  }))
  while (stages.length < 3) {
    stages.push({title: '', dateRange: ''})
  }
  const maxStage = resolveMaxMatchStage(enableGlobal)
  const progressIdx = matchInfo.findIndex((item) => item.is_progress)
  let progressIndex: 0 | 1 | 2 = 0
  if (homeStatus === MiddleContestHomeStatus.review && enableGlobal) {
    progressIndex = MiddleContestHomeMatchStage.nationalAssist
  } else if (progressIdx >= 0) {
    progressIndex = Math.min(progressIdx, maxStage) as 0 | 1 | 2
  }
  return {
    stages: stages as [
      {title: string; dateRange: string},
      {title: string; dateRange: string},
      {title: string; dateRange: string}
    ],
    progressIndex,
    hideGlobalStage: !enableGlobal,
  }
}

/** buff 剩余秒数 -> 倒计时结束时间戳 */
export const getBuffCountdownEndTime = (buffExpireSeconds: number): number => {
  if (!buffExpireSeconds || buffExpireSeconds <= 0) return 0
  return Date.now() + buffExpireSeconds * 1000
}

/** 抢位赛 countdown 剩余秒数 -> 倒计时结束时间戳 */
export const getRepechageCountdownEndTime = (
  countdownSeconds: number
): number => {
  if (!countdownSeconds || countdownSeconds <= 0) return 0
  return Date.now() + countdownSeconds * 1000
}

export const normalizeRepechageUnionInfo = (
  union?: MiddleContestRepechageUnionInfo | null
): MiddleContestRepechageUnionInfo => ({
  union_id: union?.union_id ?? 0,
  union_icon: union?.union_icon ?? '',
  union_name: union?.union_name ?? '',
  group: union?.group ?? '',
  score: union?.score ?? 0,
})

const normalizeRepechageRewardItem = (
  item: MiddleContestRepechageRewardItem
): MiddleContestRepechageRewardItem => ({
  id: item.id ?? 0,
  name: item.name ?? '',
  view_icon: item.view_icon ?? '',
  toast_name: item.toast_name ?? '',
  reward_type: item.reward_type ?? 0,
  tag: item.tag ?? '',
  quantity: item.quantity ?? 0,
  reward_value: item.reward_value ?? 0,
  has_show_effect: !!item.has_show_effect,
  ...(item.privilege_id != null ? {privilege_id: item.privilege_id} : {}),
  ...(item.days != null ? {days: item.days} : {}),
})

const normalizeRepechageSuccessReward = (
  reward?: MiddleContestRepechageSuccessReward | null
): MiddleContestRepechageSuccessReward | null => {
  if (!reward) {
    return null
  }
  return {
    income_threshold: reward.income_threshold ?? 0,
    reward_list: (reward.reward_list ?? []).map(normalizeRepechageRewardItem),
    my_group_list: (reward.my_group_list ?? []).map(
      normalizeRepechageRewardItem
    ),
    except_group_list: (reward.except_group_list ?? []).map(
      normalizeRepechageRewardItem
    ),
  }
}

export const normalizeRepechageResponse = (
  payload: MiddleContestRepechageResponse = {}
): MiddleContestRepechageState => ({
  err_msg: payload.err_msg ?? '',
  is_challenger: !!payload.is_challenger,
  is_defender: !!payload.is_defender,
  is_in_repechage: !!payload.is_in_repechage,
  my_union: payload.my_union
    ? normalizeRepechageUnionInfo(payload.my_union)
    : null,
  defender_union: payload.defender_union
    ? normalizeRepechageUnionInfo(payload.defender_union)
    : null,
  gap_with_prev_group_champion: payload.gap_with_prev_group_champion ?? 0,
  countdown: payload.countdown ?? 0,
  challenger_success_reward: normalizeRepechageSuccessReward(
    payload.challenger_success_reward
  ),
  defender_success_reward: normalizeRepechageSuccessReward(
    payload.defender_success_reward
  ),
})

export interface BoostCardListItem {
  id: number
  card_odds: number
  card_hours: number
  card_icon: string
  status: MiddleContestCardStatus
  expire_ts?: number
  expire_time?: string
  use_time?: string
}

/** 将接口 card_list 按状态分组，供加成卡列表 UI 使用 */
export const groupMiddleContestCards = (
  cardList: MiddleContestCardItem[] = []
): {
  using: BoostCardListItem[]
  unused: BoostCardListItem[]
  used: BoostCardListItem[]
} => {
  const mapItem = (card: MiddleContestCardItem): BoostCardListItem => ({
    id: card.card_id,
    card_odds: card.card_odds ?? 0,
    card_hours: card.card_hours ?? 0,
    card_icon: card.card_icon ?? '',
    status: card.ustatus,
    expire_ts: card.expire_ts,
    expire_time: card.expire_time ?? '',
    use_time: card.use_time ?? '',
  })

  return {
    using: cardList
      .filter((c) => c.ustatus === MiddleContestCardStatus.using)
      .map(mapItem),
    unused: cardList
      .filter((c) => c.ustatus === MiddleContestCardStatus.unused)
      .map(mapItem),
    used: cardList
      .filter((c) => c.ustatus === MiddleContestCardStatus.used)
      .map(mapItem),
  }
}

export interface MiddleContestPersonListItem {
  mid: number
  avatar: string
  name: string
  income: number
  card_icon: string
  card_mul: number
  /** 是否展示完成任务相关字段（收益/加成卡） */
  showTaskReward: boolean
}

export const mapMiddleContestPersonItem = (
  item: MiddleContestPersonItem
): MiddleContestPersonListItem => {
  const hasTaskReward =
    item.income != null || !!item.card_icon || item.card_mul != null
  return {
    mid: item.mid ?? 0,
    avatar: item.avatar ?? '',
    name: item.name ?? '',
    income: item.income ?? 0,
    card_icon: item.card_icon ?? '',
    card_mul: item.card_mul ?? 0,
    showTaskReward: hasTaskReward,
  }
}

export const mapMiddleContestPersonList = (
  list: MiddleContestPersonItem[] = []
): MiddleContestPersonListItem[] => list.map(mapMiddleContestPersonItem)

/** person_list 接口 data → Redux person.data（from 由 thunk meta.arg 写入） */
export const normalizePersonListResponse = (
  payload: MiddleContestPersonListResponse = {list: []}
): Omit<MiddleContestPersonListState, 'from'> => ({
  err_msg: payload.err_msg ?? '',
  income_threshold: payload.income_threshold ?? 0,
  can_one_click_call: payload.can_one_click_call ?? false,
  card_mul_min: payload.card_mul_min ?? 1.4,
  card_mul_max: payload.card_mul_max ?? 1.5,
  list: payload.list ?? [],
})

export const normalizeSupportAnchorMember = (
  member?: ActivityMemberV2 | null
): ActivityMemberV2 => ({
  id: member?.id ?? 0,
  name: member?.name ?? '',
  zy_id: member?.zy_id ?? '',
  avatar: member?.avatar ?? '',
  gender: member?.gender ?? 0,
})

export const normalizeSupportAnchor = (
  item: MiddleContestSupportAnchor
): MiddleContestSupportAnchor => ({
  member: normalizeSupportAnchorMember(item.member),
  rank_no: item.rank_no ?? 0,
  score: item.score ?? 0,
})

export const emptyAuditionToRankData =
  (): MiddleContestAuditionToRankState => ({
    union_id: 0,
    from: '',
    date: '',
    err_msg: '',
    offset: 0,
    more: false,
    own: null,
    support_anchors: [],
  })

export const normalizeAuditionToRankResponse = (
  payload: MiddleContestAuditionToRankResponse = {},
  params: Pick<
    MiddleContestAuditionToRankRequestParams,
    'union_id' | 'from' | 'date'
  >
): MiddleContestAuditionToRankState => ({
  union_id: params.union_id,
  from: params.from,
  date: params.date ?? '',
  err_msg: payload.err_msg ?? '',
  offset: payload.offset ?? 0,
  more: !!payload.more,
  own: payload.own ? normalizeSupportAnchor(payload.own) : null,
  support_anchors: (payload.support_anchors ?? []).map(normalizeSupportAnchor),
})

/** 动效预览 ID：优先 reward_config_id，无则回退 id，不使用 privilege_id */
export const resolveRewardEffectId = (item: {
  reward_config_id?: number | null
  id?: number | null
}): number | undefined => {
  if (item.reward_config_id != null && item.reward_config_id > 0) {
    return item.reward_config_id
  }
  if (item.id != null && item.id > 0) {
    return item.id
  }
  return undefined
}

/** 预热动效预览 activity_id：优先 reward_list 返回，回退 task_list */
export const resolvePreheatActivityId = (
  rewardActivityId?: number | null,
  taskActivityId?: number | null
): number | undefined => {
  if (rewardActivityId != null && rewardActivityId > 0) {
    return rewardActivityId
  }
  if (taskActivityId != null && taskActivityId > 0) {
    return taskActivityId
  }
  return undefined
}

export const normalizeRankUnion = (
  union?: MiddleContestRankUnion | null
): MiddleContestRankUnion => ({
  id: Number(union?.id ?? 0),
  name: union?.name ?? '',
  icon: union?.icon ?? '',
  area_code: union?.area_code,
  ...(union?.flag_icon ? {flag_icon: union.flag_icon} : {}),
})

export const normalizeRankReward = (
  item: MiddleContestRankReward
): MiddleContestRankReward => ({
  ...(item.id != null ? {id: item.id} : {}),
  name: item.name ?? '',
  icon: item.icon ?? '',
  show_type: item.show_type,
  num: item.num ?? 0,
  privilege_id: item.privilege_id ?? 0,
  privilege_type: item.privilege_type,
  reward_config_id: item.reward_config_id,
  ...(item.tag ? {tag: item.tag} : {}),
})

const isRankRewardEntry = (item: unknown): item is MiddleContestRankReward =>
  !!item &&
  typeof item === 'object' &&
  !Array.isArray(item) &&
  ('name' in item || 'icon' in item)

/** anchor_rewards 兼容一维/二维数组，归一化为二维 */
export const normalizeAnchorRewards = (
  anchorRewards?: MiddleContestRankReward[] | MiddleContestRankReward[][] | null
): MiddleContestRankReward[][] => {
  const raw = anchorRewards ?? []
  if (!raw.length) {
    return []
  }

  if (isRankRewardEntry(raw[0])) {
    return [(raw as MiddleContestRankReward[]).map(normalizeRankReward)]
  }

  return (raw as MiddleContestRankReward[][]).map((row) =>
    (row ?? []).map(normalizeRankReward)
  )
}

/** 展平 anchor_rewards（一维/二维均可） */
export const flattenAnchorRewards = (
  anchorRewards?: MiddleContestRankReward[] | MiddleContestRankReward[][]
): MiddleContestRankReward[] => normalizeAnchorRewards(anchorRewards).flat()

export const normalizeRankListItem = (
  item: MiddleContestRankListItem
): MiddleContestRankListItem => ({
  union: normalizeRankUnion(item.union),
  is_reach_next: !!item.is_reach_next,
  rank_no: item.rank_no ?? 0,
  score: item.score ?? 0,
  gap_with_prev: item.gap_with_prev ?? 0,
  reward_need_score: item.reward_need_score ?? 0,
  ...(item.speed_card_icon ? {speed_card_icon: item.speed_card_icon} : {}),
  union_leader_rewards: (item.union_leader_rewards ?? []).map(
    normalizeRankReward
  ),
  anchor_rewards: normalizeAnchorRewards(item.anchor_rewards),
})

export const normalizeRankOwn = (
  own?: MiddleContestRankOwn | null
): MiddleContestRankOwn | null => {
  if (!own?.union) {
    return null
  }
  const union = normalizeRankUnion(own.union)
  if (!union.id) {
    return null
  }
  return {
    union,
    rank_no: Number(own.rank_no ?? 0),
    score: own.score ?? 0,
    gap_with_prev: own.gap_with_prev ?? 0,
    reward_need_score: own.reward_need_score ?? 0,
    ...(own.is_reach_next != null ? {is_reach_next: !!own.is_reach_next} : {}),
    ...(own.speed_card_icon ? {speed_card_icon: own.speed_card_icon} : {}),
    union_leader_rewards: (own.union_leader_rewards ?? []).map(
      normalizeRankReward
    ),
    anchor_rewards: normalizeAnchorRewards(own.anchor_rewards),
    my_support_info: own.my_support_info
      ? normalizeSupportAnchor(own.my_support_info)
      : undefined,
  }
}

const parseAuditionToNextDateParts = (
  dateStr: string
): {year: number; month: number; day: number} | null => {
  const trimmed = dateStr.trim()
  const dashed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (dashed) {
    return {
      year: Number(dashed[1]),
      month: Number(dashed[2]),
      day: Number(dashed[3]),
    }
  }
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed)
  if (compact) {
    return {
      year: Number(compact[1]),
      month: Number(compact[2]),
      day: Number(compact[3]),
    }
  }
  return null
}

/** homepage / rank 日期 → 本地 0 点（支持 YYYYMMDD、YYYY-MM-DD） */
export const parseAuditionToNextDateString = (dateStr: string): Date | null => {
  const parts = parseAuditionToNextDateParts(dateStr)
  if (!parts) {
    return null
  }
  const {year, month, day} = parts
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

/** rank / audition_to_rank 请求用日期 YYYYMMDD */
export const formatAuditionToNextRankDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

export const normalizeAuditionToNextDateString = (dateStr: string): string => {
  const date = parseAuditionToNextDateString(dateStr)
  if (!date) {
    return dateStr
  }
  return formatAuditionToNextRankDate(date)
}

export const isSameAuditionToNextDate = (a: string, b: string): boolean => {
  const dateA = parseAuditionToNextDateString(a)
  const dateB = parseAuditionToNextDateString(b)
  if (!dateA || !dateB) {
    return a === b
  }
  return dateA.getTime() === dateB.getTime()
}

/**
 * 直通车 rank date：优先用户已选（须在 dateList 内），否则 cur_date，否则列表首项
 */
export const resolveAuditionToNextRankDate = (
  curDate: string,
  dateList: string[],
  pickedDate?: string
): string => {
  const list = dateList.filter(Boolean)
  const findInList = (target: string) =>
    list.find((item) => isSameAuditionToNextDate(item, target))

  if (pickedDate) {
    const matched = findInList(pickedDate)
    if (matched) {
      return matched
    }
  }
  if (curDate) {
    const matched = findInList(curDate)
    if (matched) {
      return matched
    }
  }
  return list[0] ?? ''
}

export const emptyRankData = (): MiddleContestRankState => ({
  from: '',
  tab: 0,
  date: '',
  err_msg: '',
  offset: 0,
  more: false,
  group_list: [],
  list: [],
  own: null,
  rule_list: [],
  country_list: null,
  reward_need_score: 0,
})

export const emptyRewardListData = (): MiddleContestRewardListState => ({
  from: '',
  err_msg: '',
  raw: {},
})

export const normalizeRewardListResponse = (
  payload: MiddleContestRewardListResponse & Record<string, unknown> = {},
  from = ''
): MiddleContestRewardListState => {
  const {err_msg, ...rest} = payload
  return {
    from,
    err_msg: typeof err_msg === 'string' ? err_msg : '',
    raw: rest,
  }
}

export const normalizeGlobalCountryItem = (
  item: MiddleContestGlobalCountryItem = {} as MiddleContestGlobalCountryItem
): MiddleContestGlobalCountryItem => ({
  country_code: Number(item.country_code ?? 0),
  flag_icon: item.flag_icon ?? '',
  like_count: item.like_count ?? 0,
  assist_points: item.assist_points ?? 0,
  ...(item.assist_points_delta != null && item.assist_points_delta > 0
    ? {assist_points_delta: item.assist_points_delta}
    : {}),
  is_liked: !!item.is_liked,
})

export const normalizeGlobalCountryList = (
  payload?: MiddleContestGlobalCountryList | null
): MiddleContestGlobalCountryList | null => {
  const list = (payload?.list ?? [])
    .map(normalizeGlobalCountryItem)
    .filter((item) => item.country_code > 0)

  if (!list.length) {
    return null
  }

  return {
    ...(payload?.default_country_code != null
      ? {default_country_code: Number(payload.default_country_code)}
      : {}),
    list,
  }
}

export const normalizeRankResponse = (
  payload: MiddleContestRankResponse = {},
  params: Pick<MiddleContestRankRequestParams, 'from' | 'tab' | 'date'>
): MiddleContestRankState => ({
  from: params.from,
  tab: params.tab ?? 0,
  date: params.date ?? '',
  err_msg: payload.err_msg ?? '',
  offset: payload.new_offset ?? payload.offset ?? 0,
  more: !!payload.more,
  group_list: payload.group_list ?? [],
  list: (payload.list ?? []).map(normalizeRankListItem),
  own: normalizeRankOwn(payload.own),
  rule_list: (payload.rule_list ?? []).map(normalizeRankReward),
  country_list: normalizeGlobalCountryList(payload.country_list),
  reward_need_score: payload.reward_need_score ?? 0,
})

export const normalizeTaskRecordTaskItem = (
  item: MiddleContestTaskRecordTaskInfo
): MiddleContestTaskRecordTaskInfo => ({
  level: item.level ?? 0,
  target: item.target ?? 0,
  income: item.income ?? 0,
  st: item.st ?? '',
  et: item.et ?? '',
})

export const normalizeTaskRecordRewardItem = (
  item: MiddleContestTaskRecordRewardItem
): MiddleContestTaskRecordRewardItem => ({
  icon: item.icon ?? '',
  reward_time: item.reward_time ?? '',
  num: item.num ?? 0,
  name: item.name ?? '',
})

/** 任务 countdown 剩余秒数 -> 倒计时结束时间戳 */
export const getTaskCountdownEndTime = (countdownSeconds: number): number => {
  if (!countdownSeconds || countdownSeconds <= 0) return 0
  return Date.now() + countdownSeconds * 1000
}

export const normalizeTaskConfigItem = (
  task: MiddleContestTaskConfigItem
): MiddleContestTaskConfigItem => ({
  ...task,
  level: task.level ?? 0,
  target: task.target ?? 0,
  leader_reward: task.leader_reward ?? null,
  anchor_rewards: task.anchor_rewards ?? [],
  is_current: !!task.is_current,
  current_score: task.current_score ?? 0,
  countdown: task.countdown ?? 0,
  top_contributors: task.top_contributors ?? [],
  is_unlocked: !!task.is_unlocked,
  endless_mode_times: task.endless_mode_times ?? 0,
  leader_reward_label: task.leader_reward_label ?? '',
  anchor_reward_labels: task.anchor_reward_labels ?? [],
})

export const normalizeLotteryInfoItem = (
  item: MiddleContestLotteryInfo = {}
): MiddleContestLotteryInfo => ({
  err_msg: item.err_msg ?? '',
  lottery_id: item.lottery_id ?? 0,
  ticket_num: item.ticket_num ?? 0,
  ticket_type: item.ticket_type ?? 0,
  ticket_cost: item.ticket_cost ?? 0,
  lottery_limit: item.lottery_limit ?? 0,
  lottery_times: item.lottery_times ?? 0,
  prize_list: (item.prize_list ?? []).map((prize) => ({
    id: prize.id ?? 0,
    name: prize.name ?? '',
    icon: prize.icon ?? '',
    value: prize.value ?? 0,
    tag: prize.tag,
  })),
})

export const normalizeTaskListResponse = (
  payload: MiddleContestTaskListResponse = {}
): Pick<
  MiddleContestTaskState,
  'groupName' | 'list' | 'activity_id' | 'lottery_info'
> => ({
  groupName: payload.group_name ?? '',
  list: (payload.tasks ?? []).map(normalizeTaskConfigItem),
  activity_id: payload.activity_id ?? 0,
  lottery_info: (payload.lottery_info ?? []).map(normalizeLotteryInfoItem),
})

export const normalizeTaskRecordResponse = (
  payload: MiddleContestTaskRecordResponse = {}
): Pick<
  MiddleContestTaskRecordState,
  'err_msg' | 'task_list' | 'reward_list'
> => ({
  err_msg: payload.err_msg ?? '',
  task_list: (payload.task_list ?? []).map(normalizeTaskRecordTaskItem),
  reward_list: (payload.reward_list ?? []).map(normalizeTaskRecordRewardItem),
})

export const pickCardInfoScalars = (
  payload: MiddleContestCardInfoResponse
): Omit<MiddleContestCardInfoResponse, 'card_list'> => {
  const {
    is_buff_active,
    competition_phase,
    final_contest_start_time,
    current_ratio,
    buff_expire_ts,
    user_identity,
    is_remind_can_use,
    new_offset,
    more,
    err_msg,
    count,
  } = payload
  return {
    is_buff_active: !!is_buff_active,
    competition_phase: competition_phase ?? 0,
    final_contest_start_time: final_contest_start_time ?? '',
    current_ratio: current_ratio ?? 0,
    buff_expire_ts: buff_expire_ts ?? 0,
    user_identity: user_identity ?? 0,
    is_remind_can_use: !!is_remind_can_use,
    new_offset: new_offset ?? 0,
    more: !!more,
    err_msg: err_msg ?? '',
    count: count ?? 0,
  }
}
